import { useMemo, useState, useCallback } from "react";

/**
 * useTableSort — Round 30. Click-to-sort engine for tables built on the
 * R41 column-def pattern ({ id, label, type, get }).
 *
 *   const { sorted, sort, toggleSort } = useTableSort(rows, columns);
 *
 * Type-aware comparison:
 *   • numbers / currency — "$1,234.50" sorts numerically (strips $ , % and
 *     whitespace before parsing)
 *   • dates — col.type === "date", or both values parse as dates
 *     ("6/3/2026", ISO strings)
 *   • booleans — true above false
 *   • everything else — case-insensitive locale compare
 * Null/empty values always sink to the bottom in either direction.
 *
 * toggleSort cycles asc → desc → off per column. Client-side only: on
 * server-paginated tables this sorts the loaded page.
 */

const NUMERIC_RE = /^-?[$\s]*[\d,]+(\.\d+)?%?$/;

const rawValue = (col, row) => {
    if (!col) return undefined;
    if (typeof col.get === "function") return col.get(row);
    const k = col.key || col.id;
    return row ? row[k] : undefined;
};

const asNumber = (v) => {
    if (typeof v === "number") return v;
    if (typeof v !== "string") return NaN;
    const t = v.trim();
    if (!NUMERIC_RE.test(t)) return NaN;
    return parseFloat(t.replace(/[$,%\s]/g, ""));
};

const asDate = (v) => {
    if (v instanceof Date) return v.getTime();
    if (typeof v !== "string" || v.trim() === "") return NaN;
    const t = Date.parse(v);
    return Number.isNaN(t) ? NaN : t;
};

export function compareCells(a, b, type) {
    const aEmpty = a === null || a === undefined || a === "";
    const bEmpty = b === null || b === undefined || b === "";
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;   // empties sink regardless of direction
    if (bEmpty) return -1;

    if (typeof a === "boolean" || typeof b === "boolean") {
        return (b === true ? 1 : 0) - (a === true ? 1 : 0);
    }
    const na = asNumber(a), nb = asNumber(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;

    if (type === "date") {
        const da = asDate(a), db = asDate(b);
        if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;
    } else {
        const da = asDate(a), db = asDate(b);
        const looksDate = typeof a === "string" && typeof b === "string" &&
            /[\/\-:]/.test(a) && /[\/\-:]/.test(b);
        if (looksDate && !Number.isNaN(da) && !Number.isNaN(db)) return da - db;
    }
    return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
}

export default function useTableSort(rows, columns) {
    const [sort, setSort] = useState(null); // { id, dir: 1 | -1 } | null

    const toggleSort = useCallback((id) => {
        setSort((s) => {
            if (!s || s.id !== id) return { id, dir: 1 };
            if (s.dir === 1) return { id, dir: -1 };
            return null;
        });
    }, []);

    const sorted = useMemo(() => {
        const list = Array.isArray(rows) ? rows : [];
        if (!sort) return list;
        const col = (columns || []).find((c) => c.id === sort.id);
        if (!col) return list;
        const copy = [...list];
        copy.sort((ra, rb) => {
            const cmp = compareCells(rawValue(col, ra), rawValue(col, rb), col.type);
            // empties pinned to bottom: don't flip their ordering with dir
            const aEmpty = [null, undefined, ""].includes(rawValue(col, ra));
            const bEmpty = [null, undefined, ""].includes(rawValue(col, rb));
            if (aEmpty || bEmpty) return cmp;
            return cmp * sort.dir;
        });
        return copy;
    }, [rows, columns, sort]);

    return { sorted, sort, toggleSort };
}
